import { useState, useEffect, useCallback } from 'react'
import {
    getEmpresas,
    getEmpresaById,
    createEmpresa,
    updateEmpresa,
    deleteEmpresa
} from '@shared/helpers/api/mantenedores/empresas'
import { getTiposDocumento } from '@helpers/api/mantenedores/generales'
import { useSelector } from 'react-redux'

/**
 * Hook para manejar el mantenedor de empresas
 * - Obtiene la lista de empresas
 * - Permite obtener, crear, actualizar y eliminar empresas
 * - Maneja loading y error states
 */
const useEmpresas = () => {
    const user = useSelector(state => state.auth.user)
    const [empresas, setEmpresas] = useState([])
    const [loading, setLoading] = useState(false)
    const [modalLoading, setModalLoading] = useState(false)
    const [error, setError] = useState(null)
    const [selectedEmpresa, setSelectedEmpresa] = useState(null)
    const [tiposDocumento, setTiposDocumento] = useState([])

    // Obtener todas las empresas
    const fetchEmpresas = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const [tiposDocumento, empresas] = await Promise.all([
                getTiposDocumento(user, { pageSize: 100 }),
                getEmpresas(user)
            ])
            if (tiposDocumento.success) setTiposDocumento(tiposDocumento.data)
            if (empresas.success) setEmpresas(empresas.data)
        } catch (error) {
            setError(error.message || 'Error al obtener datos')
        }
        setLoading(false)
    }, [user])

    // Obtener empresa específica
    const fetchEmpresaById = useCallback(
        async id => {
            setModalLoading(true)
            setError(null)
            const res = await getEmpresaById(user, id)
            if (res.success) {
                setSelectedEmpresa(res.data)
            } else {
                setError(res.error || 'Error al obtener empresa')
            }
            setModalLoading(false)
        },
        [user]
    )

    // Crear empresa
    const handleCreateEmpresa = useCallback(
        async empresaData => {
            setLoading(true)
            setError(null)
            const res = await createEmpresa(user, empresaData)
            setLoading(false)
            return res
        },
        [user]
    )

    // Actualizar empresa
    const handleUpdateEmpresa = useCallback(
        async (id, empresaData) => {
            setModalLoading(true)
            setError(null)
            const res = await updateEmpresa(user, id, empresaData)
            setModalLoading(false)
            return res
        },
        [user]
    )

    // Eliminar empresa
    const handleDeleteEmpresa = useCallback(
        async id => {
            setModalLoading(true)
            setError(null)
            const res = await deleteEmpresa(user, id)
            setModalLoading(false)
            return res
        },
        [user]
    )

    useEffect(() => {
        fetchEmpresas()
    }, [fetchEmpresas])

    return {
        user,
        empresas,
        tiposDocumento,
        loading,
        modalLoading,
        error,
        selectedEmpresa,
        fetchEmpresas,
        fetchEmpresaById,
        handleCreateEmpresa,
        handleUpdateEmpresa,
        handleDeleteEmpresa,
        setSelectedEmpresa
    }
}

export default useEmpresas
